// Parte de Labores por Cuadrilla (módulo operativo de campo bananero).
// Nómina → Movimientos. Registra qué hizo cada cuadrilla (finca, labor,
// cantidad producida) y quiénes participaron (horas + producción individual).
// El resumen de productividad (rendimiento = cantidad/horas) sirve para pagar
// a destajo/tarea y para comparar cuadrillas. RPCs nom_* (esquema `nomina`).
import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ListChecks, Loader2, Plus, Settings2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import {
  Campo, Modal, fieldCls, fecha, nomRpc, useSort, SortTh, hoyISO, useDialogoEnLinea,
  type EmpleadoRow,
} from './shared';
import { ExportarBotones, type Celda } from '@/lib/utils/exportarTabla';
import { fechaLocal } from '@/lib/utils/fecha';
import { LOCALE } from '@/lib/utils/regional';

// ------------------------------------------------------------------ Tipos ----
interface FincaOpt { id: string; nombre: string }
interface LaborOpt { id: string; codigo: string | null; nombre: string; unidad: string; activo?: boolean }
interface ParteRow {
  id: string; fecha: string; centro_costo_id: string; finca: string; cuadrilla: string;
  labor_id: string; labor: string; unidad: string; cantidad: number;
  n_trabajadores: number; total_horas: number; observacion: string | null;
}
interface ParteTrabajador { empleado_id: string; nombre: string; horas: number; cantidad: number | null }
interface ParteDetalle {
  id: string; fecha: string; centro_costo_id: string; finca: string; cuadrilla: string;
  labor_id: string; labor: string; unidad: string; cantidad: number; observacion: string | null;
  trabajadores: ParteTrabajador[];
}
interface ResumenRow {
  cuadrilla: string; finca: string; labor: string; unidad: string;
  total_cantidad: number; total_horas: number; n_partes: number; n_trabajadores: number; rendimiento: number;
}

const cant = (v: number | string | null | undefined) => (v == null ? '—' : Number(v).toLocaleString(LOCALE, { maximumFractionDigits: 2 }));

interface TrabajadorForm { empleado_id: string; horas: string; cantidad: string }
const vacioForm = { fecha: hoyISO(), centro_costo_id: '', cuadrilla: '', labor_id: '', cantidad: '', observacion: '' };

export function PartesLabor() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [vista, setVista] = useState<'partes' | 'resumen'>('partes');

  const inicioMes = `${hoyISO().slice(0, 7)}-01`;
  const [desde, setDesde] = useState(inicioMes);
  const [hasta, setHasta] = useState(hoyISO());
  const [fincaId, setFincaId] = useState('');

  const { data: fincas = [] } = useQuery<FincaOpt[]>({
    queryKey: ['nom-fincas'],
    queryFn: () => nomRpc<FincaOpt[]>('nom_fincas'),
  });
  const { data: labores = [] } = useQuery<LaborOpt[]>({
    queryKey: ['nom-labores'],
    queryFn: () => nomRpc<LaborOpt[]>('nom_labores'),
  });
  const { data: empleados = [] } = useQuery<EmpleadoRow[]>({
    queryKey: ['nom-empleados-opt-act'],
    queryFn: () => nomRpc<EmpleadoRow[]>('nom_empleados', { _q: null, _solo_activos: true }),
  });

  const { data: partes = [], isFetching } = useQuery<ParteRow[]>({
    queryKey: ['nom-partes-labor', desde, hasta, fincaId],
    queryFn: () => nomRpc<ParteRow[]>('nom_partes_labor', { _desde: desde, _hasta: hasta, _centro_costo_id: fincaId || null }),
    enabled: vista === 'partes',
  });
  const { data: resumen = [], isFetching: isFetchingResumen } = useQuery<ResumenRow[]>({
    queryKey: ['nom-partes-labor-resumen', desde, hasta],
    queryFn: () => nomRpc<ResumenRow[]>('nom_partes_labor_resumen', { _desde: desde, _hasta: hasta }),
    enabled: vista === 'resumen',
  });

  const invalidar = useCallback(() => {
    void qc.invalidateQueries({ predicate: (q) => typeof q.queryKey[0] === 'string' && q.queryKey[0].startsWith('nom-partes-labor') });
  }, [qc]);

  const eliminar = useMutation({
    mutationFn: (id: string) => nomRpc('nom_parte_labor_eliminar', { _id: id }),
    onSuccess: () => { toast.success('Parte eliminado'); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  // ---- Modal alta/edición de parte ----
  const [abierto, setAbierto] = useState(false);
  const [editandoId, setEditandoId] = useState<string | null>(null);
  const [form, setForm] = useState(vacioForm);
  const [trabajadores, setTrabajadores] = useState<TrabajadorForm[]>([]);

  const abrirNuevo = () => {
    setEditandoId(null);
    setForm({ ...vacioForm, fecha: hoyISO(), centro_costo_id: fincaId || '' });
    setTrabajadores([]);
    setAbierto(true);
  };

  const cargarParte = useMutation({
    mutationFn: (id: string) => nomRpc<ParteDetalle>('nom_parte_labor', { _id: id }),
    onSuccess: (d) => {
      setEditandoId(d.id);
      setForm({
        fecha: d.fecha.slice(0, 10), centro_costo_id: d.centro_costo_id, cuadrilla: d.cuadrilla,
        labor_id: d.labor_id, cantidad: String(d.cantidad ?? ''), observacion: d.observacion ?? '',
      });
      setTrabajadores(d.trabajadores.map((t) => ({ empleado_id: t.empleado_id, horas: String(t.horas ?? 8), cantidad: t.cantidad == null ? '' : String(t.cantidad) })));
      setAbierto(true);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const laborSel = labores.find((l) => l.id === form.labor_id) || null;

  const agregarTrabajador = () => setTrabajadores((ts) => [...ts, { empleado_id: '', horas: '8', cantidad: '' }]);
  const quitarTrabajador = (idx: number) => setTrabajadores((ts) => ts.filter((_, i) => i !== idx));
  const setTrabajador = (idx: number, patch: Partial<TrabajadorForm>) =>
    setTrabajadores((ts) => ts.map((t, i) => (i === idx ? { ...t, ...patch } : t)));

  const empleadosDisponibles = (idxActual: number) => {
    const usados = new Set(trabajadores.filter((_, i) => i !== idxActual).map((t) => t.empleado_id).filter(Boolean));
    return empleados.filter((e) => !usados.has(e.id));
  };

  const guardar = useMutation({
    mutationFn: () => nomRpc('nom_parte_labor_guardar', {
      _data: {
        id: editandoId || undefined,
        fecha: form.fecha,
        centro_costo_id: form.centro_costo_id,
        cuadrilla: form.cuadrilla,
        labor_id: form.labor_id,
        cantidad: Number(form.cantidad) || 0,
        observacion: form.observacion || null,
        trabajadores: trabajadores.filter((t) => t.empleado_id).map((t) => ({
          empleado_id: t.empleado_id,
          horas: Number(t.horas) || 0,
          cantidad: t.cantidad === '' ? null : Number(t.cantidad),
        })),
      },
    }),
    onSuccess: () => { toast.success(editandoId ? 'Parte actualizado' : 'Parte registrado'); setAbierto(false); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const formValido = !!form.fecha && !!form.centro_costo_id && !!form.cuadrilla.trim() && !!form.labor_id
    && trabajadores.length > 0 && trabajadores.every((t) => t.empleado_id && Number(t.horas) >= 0);

  // ---- Tabla de partes (ordenable) ----
  const getValParte = useCallback((r: ParteRow, key: string): string | number | null => {
    switch (key) {
      case 'fecha': return r.fecha;
      case 'finca': return r.finca;
      case 'cuadrilla': return r.cuadrilla;
      case 'labor': return r.labor;
      case 'cantidad': return Number(r.cantidad);
      case 'n_trabajadores': return Number(r.n_trabajadores);
      case 'total_horas': return Number(r.total_horas);
      default: return null;
    }
  }, []);
  const sortPartes = useSort<ParteRow>(partes, getValParte, 'fecha', 'desc');

  const totalCantidadPartes = partes.reduce((s, p) => s + Number(p.cantidad), 0);
  const totalHorasPartes = partes.reduce((s, p) => s + Number(p.total_horas), 0);

  const headersExpPartes = ['N°', 'Fecha', 'Finca', 'Cuadrilla', 'Labor', 'Cantidad', 'Unidad', 'Trabajadores', 'Horas', 'Observación'];
  const filasExpPartes: Celda[][] = [
    ...sortPartes.sorted.map((p, i): Celda[] => [
      i + 1, fecha(p.fecha), p.finca, p.cuadrilla, p.labor, Number(p.cantidad), p.unidad, p.n_trabajadores, Number(p.total_horas), p.observacion ?? '',
    ]),
    ['', '', '', '', 'TOTAL', totalCantidadPartes, '', '', totalHorasPartes, ''],
  ];

  // ---- Resumen de productividad ----
  const getValResumen = useCallback((r: ResumenRow, key: string): string | number | null => {
    switch (key) {
      case 'cuadrilla': return r.cuadrilla;
      case 'finca': return r.finca;
      case 'labor': return r.labor;
      case 'total_cantidad': return Number(r.total_cantidad);
      case 'total_horas': return Number(r.total_horas);
      case 'n_partes': return Number(r.n_partes);
      case 'n_trabajadores': return Number(r.n_trabajadores);
      case 'rendimiento': return Number(r.rendimiento);
      default: return null;
    }
  }, []);
  const sortResumen = useSort<ResumenRow>(resumen, getValResumen);

  const totalCantidadRes = resumen.reduce((s, r) => s + Number(r.total_cantidad), 0);
  const totalHorasRes = resumen.reduce((s, r) => s + Number(r.total_horas), 0);
  const totalPartesRes = resumen.reduce((s, r) => s + Number(r.n_partes), 0);

  const headersExpResumen = ['N°', 'Cuadrilla', 'Finca', 'Labor', 'Unidad', 'Total cantidad', 'Total horas', 'N.º partes', 'N.º trabajadores', 'Rendimiento'];
  const filasExpResumen: Celda[][] = [
    ...sortResumen.sorted.map((r, i): Celda[] => [
      i + 1, r.cuadrilla, r.finca, r.labor, r.unidad, Number(r.total_cantidad), Number(r.total_horas), r.n_partes, r.n_trabajadores, Number(r.rendimiento),
    ]),
    ['', '', '', '', 'TOTAL', totalCantidadRes, totalHorasRes, totalPartesRes, '', ''],
  ];

  // ---- Modal gestionar labores ----
  const [gestLabores, setGestLabores] = useState(false);

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ListChecks className="w-7 h-7 text-primary" />
          <div>
            <h1 className="text-xl font-semibold">Parte de labores por cuadrilla</h1>
            <p className="text-sm text-muted-foreground">Registro diario de labores de campo y productividad por cuadrilla</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setGestLabores(true)}><Settings2 className="w-4 h-4 mr-1" /> Gestionar labores</Button>
          {vista === 'partes' && <Button onClick={abrirNuevo}><Plus className="w-4 h-4 mr-1" /> Nuevo parte</Button>}
        </div>
      </div>

      <div className="flex gap-1 border-b">
        {([['partes', 'Partes'], ['resumen', 'Resumen de productividad']] as const).map(([clave, etiqueta]) => (
          <button
            key={clave}
            onClick={() => setVista(clave)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              vista === clave ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {etiqueta}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <Campo label="Desde"><input type="date" className={fieldCls} value={desde} onChange={(e) => setDesde(e.target.value)} /></Campo>
        <Campo label="Hasta"><input type="date" className={fieldCls} value={hasta} onChange={(e) => setHasta(e.target.value)} /></Campo>
        {vista === 'partes' && (
          <Campo label="Finca">
            <select className={`${fieldCls} w-56`} value={fincaId} onChange={(e) => setFincaId(e.target.value)}>
              <option value="">Todas</option>
              {fincas.map((f) => <option key={f.id} value={f.id}>{f.nombre}</option>)}
            </select>
          </Campo>
        )}
      </div>

      {vista === 'partes' && (
        <Card>
          <CardHeader className="pb-2 flex-row items-center justify-between">
            <CardTitle className="text-base">Partes ({partes.length})</CardTitle>
            <ExportarBotones
              nombre={`partes-labor-${fechaLocal()}`} titulo="Parte de labores por cuadrilla"
              headers={headersExpPartes} filas={filasExpPartes} colsDerecha={[0, 5, 7, 8]} disabled={partes.length === 0}
              fmtPDF={(v, c) => (c === 5 || c === 8 ? (v === '' ? '' : cant(Number(v))) : String(v ?? ''))}
            />
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3 text-right">N.º</th>
                <SortTh col="fecha" sort={sortPartes}>Fecha</SortTh>
                <SortTh col="finca" sort={sortPartes}>Finca</SortTh>
                <SortTh col="cuadrilla" sort={sortPartes}>Cuadrilla</SortTh>
                <SortTh col="labor" sort={sortPartes}>Labor</SortTh>
                <SortTh col="cantidad" sort={sortPartes} align="right">Cantidad</SortTh>
                <SortTh col="n_trabajadores" sort={sortPartes} align="right">Trabajadores</SortTh>
                <SortTh col="total_horas" sort={sortPartes} align="right">Horas</SortTh>
                <th className="py-2 pr-3">Observación</th>
                <th className="py-2"></th>
              </tr></thead>
              <tbody>
                {sortPartes.sorted.length === 0 && !isFetching && (
                  <tr><td colSpan={10} className="py-8 text-center text-muted-foreground">Sin partes en el rango</td></tr>
                )}
                {sortPartes.sorted.map((p, i) => (
                  <tr key={p.id} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                    <td className="py-2 pr-3 font-mono">{fecha(p.fecha)}</td>
                    <td className="py-2 pr-3">{p.finca}</td>
                    <td className="py-2 pr-3">{p.cuadrilla}</td>
                    <td className="py-2 pr-3">{p.labor}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{cant(p.cantidad)} <span className="text-xs text-muted-foreground">{p.unidad}</span></td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{p.n_trabajadores}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{cant(p.total_horas)}</td>
                    <td className="py-2 pr-3 text-muted-foreground">{p.observacion ?? ''}</td>
                    <td className="py-2 text-right whitespace-nowrap">
                      <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => cargarParte.mutate(p.id)}>✎</button>
                      <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-destructive hover:bg-destructive/10 ml-1" title="Eliminar"
                        onClick={async () => { if (await dlg.confirmar('¿Eliminar este parte de labor?', { peligro: true, aceptar: 'Eliminar' })) eliminar.mutate(p.id); }}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              {sortPartes.sorted.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 font-semibold bg-muted/30">
                    <td className="py-2 pr-3" colSpan={5}>Totales ({sortPartes.sorted.length})</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{cant(totalCantidadPartes)}</td>
                    <td className="py-2 pr-3"></td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{cant(totalHorasPartes)}</td>
                    <td className="py-2 pr-3" colSpan={2}></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </CardContent>
        </Card>
      )}

      {vista === 'resumen' && (
        <Card>
          <CardHeader className="pb-2 flex-row items-center justify-between">
            <CardTitle className="text-base">Resumen de productividad ({resumen.length})</CardTitle>
            <ExportarBotones
              nombre={`partes-labor-resumen-${fechaLocal()}`} titulo="Resumen de productividad por cuadrilla"
              headers={headersExpResumen} filas={filasExpResumen} colsDerecha={[0, 5, 6, 7, 8, 9]} disabled={resumen.length === 0}
              fmtPDF={(v, c) => ([5, 6, 9].includes(c) ? (v === '' ? '' : cant(Number(v))) : String(v ?? ''))}
            />
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {isFetchingResumen && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3 text-right">N.º</th>
                <SortTh col="cuadrilla" sort={sortResumen}>Cuadrilla</SortTh>
                <SortTh col="finca" sort={sortResumen}>Finca</SortTh>
                <SortTh col="labor" sort={sortResumen}>Labor</SortTh>
                <SortTh col="total_cantidad" sort={sortResumen} align="right">Total cantidad</SortTh>
                <SortTh col="total_horas" sort={sortResumen} align="right">Total horas</SortTh>
                <SortTh col="n_partes" sort={sortResumen} align="right">N.º partes</SortTh>
                <SortTh col="n_trabajadores" sort={sortResumen} align="right">N.º trabajadores</SortTh>
                <SortTh col="rendimiento" sort={sortResumen} align="right" tip="Total cantidad ÷ total horas">Rendimiento</SortTh>
              </tr></thead>
              <tbody>
                {sortResumen.sorted.length === 0 && !isFetchingResumen && (
                  <tr><td colSpan={9} className="py-8 text-center text-muted-foreground">Sin datos en el rango</td></tr>
                )}
                {sortResumen.sorted.map((r, i) => (
                  <tr key={`${r.cuadrilla}-${r.finca}-${r.labor}-${i}`} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                    <td className="py-2 pr-3">{r.cuadrilla}</td>
                    <td className="py-2 pr-3">{r.finca}</td>
                    <td className="py-2 pr-3">{r.labor}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{cant(r.total_cantidad)} <span className="text-xs text-muted-foreground">{r.unidad}</span></td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{cant(r.total_horas)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{r.n_partes}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{r.n_trabajadores}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums font-semibold">{cant(r.rendimiento)}</td>
                  </tr>
                ))}
              </tbody>
              {sortResumen.sorted.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 font-semibold bg-muted/30">
                    <td className="py-2 pr-3" colSpan={4}>Totales ({sortResumen.sorted.length})</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{cant(totalCantidadRes)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{cant(totalHorasRes)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{totalPartesRes}</td>
                    <td className="py-2 pr-3" colSpan={2}></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </CardContent>
        </Card>
      )}

      {abierto && (
        <Modal title={editandoId ? 'Editar parte de labor' : 'Nuevo parte de labor'} onClose={() => setAbierto(false)} wide>
          <div className="space-y-3">
            <div className="grid grid-cols-4 gap-3">
              <Campo label="Fecha *"><input type="date" className={fieldCls} value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} /></Campo>
              <Campo label="Finca *">
                <select className={fieldCls} value={form.centro_costo_id} onChange={(e) => setForm({ ...form, centro_costo_id: e.target.value })}>
                  <option value="">— Selecciona —</option>
                  {fincas.map((f) => <option key={f.id} value={f.id}>{f.nombre}</option>)}
                </select>
              </Campo>
              <Campo label="Cuadrilla *">
                <input className={fieldCls} value={form.cuadrilla} onChange={(e) => setForm({ ...form, cuadrilla: e.target.value })} placeholder="CUADRILLA 1" />
              </Campo>
              <Campo label="Labor *">
                <select className={fieldCls} value={form.labor_id} onChange={(e) => setForm({ ...form, labor_id: e.target.value })}>
                  <option value="">— Selecciona —</option>
                  {labores.filter((l) => l.activo !== false).map((l) => <option key={l.id} value={l.id}>{l.nombre} ({l.unidad})</option>)}
                </select>
              </Campo>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Campo label={`Cantidad producida${laborSel ? ` (${laborSel.unidad})` : ''} *`}>
                <input type="number" min="0" step="0.01" className={fieldCls} value={form.cantidad} onChange={(e) => setForm({ ...form, cantidad: e.target.value })} />
              </Campo>
              <Campo label="Observación"><input className={fieldCls} value={form.observacion} onChange={(e) => setForm({ ...form, observacion: e.target.value })} /></Campo>
            </div>

            <div className="pt-2">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Trabajadores *</span>
                <Button variant="outline" size="sm" onClick={agregarTrabajador} disabled={empleados.length === 0}>
                  <Plus className="w-4 h-4 mr-1" /> Agregar
                </Button>
              </div>
              {trabajadores.length === 0 && <div className="text-sm text-muted-foreground py-3 text-center border rounded-md">Sin trabajadores agregados</div>}
              {trabajadores.length > 0 && (
                <div className="space-y-2">
                  {trabajadores.map((t, idx) => (
                    <div key={idx} className="grid grid-cols-[1fr_120px_120px_auto] gap-2 items-end">
                      <Campo label="Empleado">
                        <select className={fieldCls} value={t.empleado_id} onChange={(e) => setTrabajador(idx, { empleado_id: e.target.value })}>
                          <option value="">— Selecciona —</option>
                          {empleadosDisponibles(idx).map((e) => <option key={e.id} value={e.id}>{e.razon_social} ({e.identificacion})</option>)}
                          {t.empleado_id && !empleadosDisponibles(idx).some((e) => e.id === t.empleado_id) && (
                            (() => { const e = empleados.find((x) => x.id === t.empleado_id); return e ? <option value={e.id}>{e.razon_social} ({e.identificacion})</option> : null; })()
                          )}
                        </select>
                      </Campo>
                      <Campo label="Horas"><input type="number" min="0" step="0.5" className={fieldCls} value={t.horas} onChange={(e) => setTrabajador(idx, { horas: e.target.value })} /></Campo>
                      <Campo label="Cantidad ind."><input type="number" min="0" step="0.01" className={fieldCls} value={t.cantidad} onChange={(e) => setTrabajador(idx, { cantidad: e.target.value })} /></Campo>
                      <button className="inline-flex w-9 h-9 items-center justify-center rounded-md text-destructive hover:bg-destructive/10 mb-0.5" title="Quitar" onClick={() => quitarTrabajador(idx)}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={guardar.isPending} disabled={!formValido} onClick={() => guardar.mutate()}>
                {editandoId ? 'Guardar cambios' : 'Registrar parte'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {gestLabores && <GestionarLaboresModal labores={labores} onClose={() => setGestLabores(false)} />}
    </div>
  );
}

// ------------------------------------------------------------- Labores -----
function GestionarLaboresModal({ labores, onClose }: { labores: LaborOpt[]; onClose: () => void }) {
  const qc = useQueryClient();
  const [editando, setEditando] = useState<LaborOpt | null>(null);
  const [form, setForm] = useState({ codigo: '', nombre: '', unidad: '', activo: true });

  const nuevo = () => { setEditando({ id: '', codigo: null, nombre: '', unidad: '', activo: true }); setForm({ codigo: '', nombre: '', unidad: '', activo: true }); };
  const editar = (l: LaborOpt) => { setEditando(l); setForm({ codigo: l.codigo ?? '', nombre: l.nombre, unidad: l.unidad, activo: l.activo !== false }); };

  const guardar = useMutation({
    mutationFn: () => nomRpc('nom_labor_guardar', {
      _data: { id: editando?.id || undefined, codigo: form.codigo || null, nombre: form.nombre, unidad: form.unidad, activo: form.activo },
    }),
    onSuccess: () => { toast.success('Labor guardada'); setEditando(null); void qc.invalidateQueries({ queryKey: ['nom-labores'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal title="Gestionar labores" onClose={onClose}>
      <div className="space-y-3">
        <div className="flex justify-end">
          <Button size="sm" onClick={nuevo}><Plus className="w-4 h-4 mr-1" /> Nueva labor</Button>
        </div>
        <div className="max-h-72 overflow-y-auto border rounded-md">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground bg-muted/30">
              <th className="py-2 px-3">Código</th><th className="py-2 px-3">Nombre</th><th className="py-2 px-3">Unidad</th>
              <th className="py-2 px-3">Estado</th><th className="py-2 px-3"></th>
            </tr></thead>
            <tbody>
              {labores.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">Sin labores registradas</td></tr>}
              {labores.map((l) => (
                <tr key={l.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-1.5 px-3 font-mono">{l.codigo ?? '—'}</td>
                  <td className="py-1.5 px-3">{l.nombre}</td>
                  <td className="py-1.5 px-3">{l.unidad}</td>
                  <td className="py-1.5 px-3">{l.activo === false ? <span className="text-muted-foreground">inactiva</span> : <span className="text-completado">activa</span>}</td>
                  <td className="py-1.5 px-3 text-right"><button className="text-primary hover:underline" onClick={() => editar(l)}>Editar</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {editando && (
          <div className="border-t pt-3 space-y-3">
            <div className="text-sm font-medium">{editando.id ? 'Editar labor' : 'Nueva labor'}</div>
            <div className="grid grid-cols-3 gap-3">
              <Campo label="Código"><input className={fieldCls} value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value })} /></Campo>
              <Campo label="Nombre *"><input className={fieldCls} value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} /></Campo>
              <Campo label="Unidad *"><input className={fieldCls} value={form.unidad} onChange={(e) => setForm({ ...form, unidad: e.target.value })} placeholder="jornal, racimos, cajas, ha…" /></Campo>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.activo} onChange={(e) => setForm({ ...form, activo: e.target.checked })} /> Activa
            </label>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditando(null)}>Cancelar</Button>
              <Button isLoading={guardar.isPending} disabled={!form.nombre.trim() || !form.unidad.trim()} onClick={() => guardar.mutate()}>Guardar</Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
